import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { getSubscriptionService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableQueryParams, TableDataResponse } from '../types/table';
import type {
  SubscriptionPlan,
  SubscriptionPlanCreateRequest,
  SubscriptionPlanUpdateRequest,
  SubscriptionPlanQueryParams,
  SubscriptionCreateRequest,
  Subscription,
} from '../types/api/subscription';

/**
 * Hook for fetching subscription plans (for DataTable)
 */
export function useSubscriptionPlans() {
  const subscriptionService = getSubscriptionService();

  const fetchSubscriptionPlans: FetchDataFn<SubscriptionPlan> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<SubscriptionPlan>> => {
      const apiParams: SubscriptionPlanQueryParams = {
        page: params.page, // 1-based
        size: params.pageSize,
      };

      if (params.search) {
        // If search is provided, we can filter by name (you may need to adjust based on your API)
        // For now, we'll just pass the search as-is if the API supports it
        // Note: The API doesn't have a search parameter, so we'll skip this
      }

      const response = await subscriptionService.getSubscriptionPlans(apiParams);

      return {
        data: response.items,
        pagination: {
          page: response.page,
          pageSize: response.size,
          total: response.totalItems,
          totalPages: response.totalPages,
          hasNext: response.hasNext,
          hasPrev: response.hasPrevious,
        },
      };
    },
    [subscriptionService]
  );

  return { fetchSubscriptionPlans };
}

/**
 * Hook for subscription plan CRUD actions
 */
export function useSubscriptionPlanActions() {
  const subscriptionService = getSubscriptionService();

  const createSubscriptionPlan = useCallback(
    async (payload: SubscriptionPlanCreateRequest): Promise<SubscriptionPlan> => {
      try {
        const plan = await subscriptionService.createSubscriptionPlan(payload);
        toast.success('Subscription plan created successfully');
        return plan;
      } catch (error: any) {
        toast.error(error.message || 'Failed to create subscription plan');
        throw error;
      }
    },
    [subscriptionService]
  );

  const getSubscriptionPlan = useCallback(
    async (planId: string): Promise<SubscriptionPlan> => {
      try {
        return await subscriptionService.getSubscriptionPlan(planId);
      } catch (error: any) {
        toast.error(error.message || 'Failed to fetch subscription plan');
        throw error;
      }
    },
    [subscriptionService]
  );

  const updateSubscriptionPlan = useCallback(
    async (planId: string, payload: SubscriptionPlanUpdateRequest): Promise<SubscriptionPlan> => {
      try {
        const plan = await subscriptionService.updateSubscriptionPlan(planId, payload);
        toast.success('Subscription plan updated successfully');
        return plan;
      } catch (error: any) {
        toast.error(error.message || 'Failed to update subscription plan');
        throw error;
      }
    },
    [subscriptionService]
  );

  const activateSubscriptionPlan = useCallback(
    async (planId: string): Promise<SubscriptionPlan> => {
      try {
        const plan = await subscriptionService.activateSubscriptionPlan(planId);
        toast.success('Subscription plan activated successfully');
        return plan;
      } catch (error: any) {
        toast.error(error.message || 'Failed to activate subscription plan');
        throw error;
      }
    },
    [subscriptionService]
  );

  const deactivateSubscriptionPlan = useCallback(
    async (planId: string): Promise<SubscriptionPlan> => {
      try {
        const plan = await subscriptionService.deactivateSubscriptionPlan(planId);
        toast.success('Subscription plan deactivated successfully');
        return plan;
      } catch (error: any) {
        toast.error(error.message || 'Failed to deactivate subscription plan');
        throw error;
      }
    },
    [subscriptionService]
  );

  const deleteSubscriptionPlan = useCallback(
    async (planId: string): Promise<void> => {
      try {
        await subscriptionService.deleteSubscriptionPlan(planId);
        toast.success('Subscription plan deleted successfully');
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete subscription plan');
        throw error;
      }
    },
    [subscriptionService]
  );

  return {
    createSubscriptionPlan,
    getSubscriptionPlan,
    updateSubscriptionPlan,
    activateSubscriptionPlan,
    deactivateSubscriptionPlan,
    deleteSubscriptionPlan,
  };
}

/**
 * Hook for user subscription actions (admin granting subscriptions)
 */
export function useSubscriptionActions() {
  const subscriptionService = getSubscriptionService();

  const createSubscription = useCallback(
    async (payload: SubscriptionCreateRequest): Promise<Subscription> => {
      try {
        const subscription = await subscriptionService.createSubscription(payload);
        toast.success('Subscription created successfully for user');
        return subscription;
      } catch (error: any) {
        toast.error(error.message || 'Failed to create subscription');
        throw error;
      }
    },
    [subscriptionService]
  );

  return { createSubscription };
}

